// ============================================
// Creator Module - Save/Publish (ES6)
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import {
  getCurrentEditorData,
  getIntroBlocks,
  getInitialEditorState, setInitialEditorState,
  getCurrentFilter,
  getCreatorDb,
  getCurrentCreations,
  getExampleDialogues,
  getSituationalImages,
  getLorebookEntries
} from './state.js';
import { ENDPOINTS } from './api.js';
import { authFetch } from '../shared/auth-fetch.js';
import { loadCreations } from './list.js';
import { closeEditor, switchEditorTab } from './editor.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { blocksToFirstMessage } from './intro-blocks.js';
import { getCurrentUser } from '../app/state.js';
import { getVisibilityValue } from './editor/events.js';

// ============================================
// 저장/등록
// ============================================

export async function saveDraft() {
  await saveCreationData('draft');
}

export async function publishCreation() {
  // 필수 필드 검증 (정보 탭)
  const charName = document.getElementById('editorCharName')?.value?.trim();
  const charGender = document.getElementById('editorCharGender')?.value;
  const charPrompt = document.getElementById('editorCharPrompt')?.value?.trim();

  if (!charName || !charGender || !charPrompt) {
    showToast('정보를 모두 입력해주세요');
    switchEditorTab('info');
    return;
  }

  // 필수 필드 검증 (인트로 탭)
  const hasIntro = getIntroBlocks().length > 0;
  if (!hasIntro) {
    showToast('인트로를 작성해주세요');
    switchEditorTab('intro');
    return;
  }

  // 필수 필드 검증 (소개 탭) - 커스텀 설명은 선택 필드
  const target = document.getElementById('editorTargetAudience')?.value;
  const shortIntro = document.getElementById('editorShortIntro')?.value?.trim();

  if (!target) {
    showToast('타겟을 선택해주세요');
    switchEditorTab('description');
    return;
  }

  if (!shortIntro) {
    showToast('한 줄 소개를 입력해주세요');
    switchEditorTab('description');
    return;
  }

  // 공개 설정 값 가져오기
  const visibility = getVisibilityValue();
  await saveCreationData(visibility);
}

export async function saveCreationData(status, options = {}) {
  const { silent = false, skipClose = false, skipRefresh = false } = options;

  const user = getCurrentUser();
  if (!user) {
    showToast('로그인이 필요합니다');
    return { success: false };
  }

  // 캐릭터 정보 수집
  const charName = document.getElementById('editorCharName')?.value?.trim() || '';
  const charGender = document.getElementById('editorCharGender')?.value || '';
  const experience = document.getElementById('editorExperience')?.value?.trim() || '';
  const charPrompt = document.getElementById('editorCharPrompt')?.value?.trim() || '';
  const charAdditionalPrompt = document.getElementById('editorCharAdditionalPrompt')?.value?.trim() || '';
  const charVoiceId = document.getElementById('editorVoiceId')?.value || '';

  // 인트로 블록을 firstMessage로 변환 (이미지는 인덱스, URL은 별도 배열)
  const { firstMessage: firstMsg, introImages } = blocksToFirstMessage();

  // 대화 예시
  const exampleDialogues = getExampleDialogues();

  // 상황별 이미지
  const situationalImages = getSituationalImages().map(img => ({
    imageUrl: img.imageUrl || img.url,
    description: img.description
  }));

  // 로어북
  const lorebook = getLorebookEntries().map(entry => ({
    name: entry.name,
    keywords: entry.keywords,
    content: entry.content,
    enabled: entry.enabled !== false
  }));

  // 소개 정보
  const charDescription = document.getElementById('editorCharDescription')?.value?.trim() || '';
  const targetAudience = document.getElementById('editorTargetAudience')?.value || 'all';
  const shortIntro = document.getElementById('editorShortIntro')?.value?.trim() || '';
  const hashtagsRaw = document.getElementById('editorHashtags')?.value?.trim() || '';
  const creatorComment = document.getElementById('editorCreatorComment')?.value?.trim() || '';
  const safetyFilter = document.getElementById('editorSafetyFilter')?.checked ?? true;
  const showThoughts = document.getElementById('editorShowThoughts')?.checked ?? false;
  const unfiltered = !safetyFilter;

  // 해시태그 파싱
  const hashtags = hashtagsRaw
    .split(/[\s,#]+/)
    .filter(tag => tag.length > 0)
    .map(tag => tag.startsWith('#') ? tag : `#${tag}`);

  // 임시저장이 아닌 경우 최소 필드 검증
  if (status !== 'draft' && !charName) {
    showToast('점술가 이름을 입력해주세요');
    return;
  }

  try {
    if (!silent) showGlobalLoading();

    // 다중 이미지 업로드 (병렬 처리)
    const currentEditorData = getCurrentEditorData();
    const introBlocks = getIntroBlocks();
    const uploadPromises = currentEditorData.images.map(async (img) => {
      if (img.file) {
        // 로컬 파일은 업로드
        return await uploadImageToStorage(img.file);
      } else if (img.url) {
        // 이미 URL인 경우 그대로 사용
        return img.url;
      }
      return null;
    });
    const uploadedImages = (await Promise.all(uploadPromises)).filter(Boolean);

    // 메인 이미지 (첫 번째 이미지)
    const imageUrl = uploadedImages.length > 0 ? uploadedImages[0] : null;

    const creationData = {
      userId: user.uid,
      creationId: currentEditorData.id,
      data: {
        // 기본 정보
        characterName: charName,
        title: charName,
        firstMessage: firstMsg,
        shortIntro,
        tags: hashtags,
        creatorComment,
        safetyFilter,
        showThoughts,
        unfiltered,
        imageUrl,
        images: uploadedImages,
        introImages,
        status,
        authorName: window.userNickname || '익명',

        // 캐릭터 설명 (소개 탭)
        characterDescription: charDescription,
        description: charDescription, // 하위 호환
        targetAudience,

        // 캐릭터 상세 정보
        character: {
          name: charName,
          gender: charGender,
          experience: experience,
          prompt: charPrompt,
          additionalPrompt: charAdditionalPrompt,
          voiceId: charVoiceId,
          imageUrl
        },

        // 인트로 블록 (편집용)
        introBlocks: introBlocks,

        // 대화 예시
        exampleDialogues: exampleDialogues,

        // 상황별 이미지
        situationalImages: situationalImages,

        // 로어북
        lorebook: lorebook
      }
    };

    const response = await authFetch(ENDPOINTS.saveCreation, {
      body: creationData
    });

    const result = await response.json();

    if (result.success) {
      // 새로 생성된 경우 ID 업데이트
      if (result.creationId && !currentEditorData.id) {
        getCurrentEditorData().id = result.creationId;
      }

      if (!silent) {
        const isUpdate = getInitialEditorState() !== null;

        let message;
        if (isUpdate && (status === 'public' || status === 'link')) {
          message = '수정되었습니다';
        } else {
          const messages = {
            draft: '임시저장되었습니다',
            private: '비공개로 저장되었습니다',
            public: '공개되었습니다',
            link: '링크 공개로 저장되었습니다'
          };
          message = messages[status] || '저장되었습니다';
        }
        showToast(message);
      }

      // 초기 상태 초기화
      setInitialEditorState(null);

      // 목록 새로고침
      if (!skipRefresh) {
        await loadCreations(getCurrentFilter());
      }

      // 에디터 닫기
      if (!skipClose) {
        closeEditor();
      }

      return { success: true };
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '저장에 실패했습니다');
      if (!silent) {
        showToast(errorMsg);
      }
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error('저장 오류:', error);
    if (!silent) {
      showToast('네트워크 오류가 발생했습니다');
    }
    return { success: false, error: error.message };
  } finally {
    if (!silent) hideGlobalLoading();
  }
}

// ============================================
// 이미지 업로드
// ============================================

export async function uploadImageToStorage(file) {
  return new Promise((resolve, reject) => {
    if (!firebase.storage) {
      // Storage SDK가 없는 경우 Data URL 반환
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const storage = firebase.storage();
    const userId = getCurrentUser()?.uid;
    const timestamp = Date.now();
    // generated 폴더에 저장하여 "내 이미지" 목록에 표시되도록 함
    const path = `creations/${userId}/generated/${timestamp}_${file.name}`;
    const ref = storage.ref(path);

    ref.put(file).then(snapshot => {
      return snapshot.ref.getDownloadURL();
    }).then(url => {
      resolve(url);
    }).catch(reject);
  });
}

// ============================================
// 상태 변경
// ============================================

export async function updateStatus(creationId, newStatus) {
  if (!getCurrentUser()) return;

  let authorName = null;
  if (newStatus === 'public') {
    authorName = window.userNickname || '익명';
  }

  try {
    showGlobalLoading();

    const response = await authFetch(ENDPOINTS.updateCreationStatus, {
      body: { creationId, newStatus, authorName }
    });

    const result = await response.json();

    if (result.success) {
      showToast(newStatus === 'public' ? '공개되었습니다' : '비공개로 전환되었습니다');
      await loadCreations(getCurrentFilter());
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '상태 변경에 실패했습니다');
      showToast(errorMsg);
    }
  } catch (error) {
    console.error('상태 변경 오류:', error);
    showToast('네트워크 오류가 발생했습니다');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 삭제
// ============================================

export async function confirmDelete(creationId) {
  const creation = getCurrentCreations().find(c => c.id === creationId);
  if (!creation) return;

  const message = creation.status === 'public'
    ? '공개된 점술가입니다. 삭제하면 다른 사용자들도 더 이상 사용할 수 없습니다. 정말 삭제하시겠습니까?'
    : '이 점술가를 삭제하시겠습니까?';

  const confirmed = await showConfirm({
    message,
    confirmText: '삭제',
    isDanger: true
  });
  if (confirmed) {
    deleteCreation(creationId);
  }
}

export async function deleteCreation(creationId) {
  if (!getCurrentUser()) return;

  try {
    showGlobalLoading();

    const response = await authFetch(ENDPOINTS.deleteCreation, {
      body: { creationId }
    });

    const result = await response.json();

    if (result.success) {
      showToast('삭제되었습니다');
      await loadCreations(getCurrentFilter());
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '삭제에 실패했습니다');
      showToast(errorMsg);
    }
  } catch (error) {
    console.error('삭제 오류:', error);
    showToast('네트워크 오류가 발생했습니다');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 복제
// ============================================

export async function duplicateCreation(creationId) {
  const user = getCurrentUser();
  if (!user || !creationId) return;

  // 메뉴 닫기
  closeItemMenu();

  const db = getCreatorDb();

  try {
    showGlobalLoading();

    // 원본 데이터 로드
    const doc = await db.collection('published').doc(creationId).get();

    if (!doc.exists) {
      showToast('원본을 찾을 수 없습니다', true);
      hideGlobalLoading();
      return;
    }

    const data = doc.data();

    // 소유자 확인
    const isOwner = data.creatorId === user.uid || data.authorId === user.uid;
    if (!isOwner) {
      showToast('본인 작품만 복제할 수 있습니다', true);
      hideGlobalLoading();
      return;
    }

    // 복제된 캐릭터 이름
    const originalName = data.character?.name || data.characterName || data.title || '점술가';
    const duplicatedName = `${originalName} - 복사`;

    // 복제 데이터 생성
    const duplicateData = {
      userId: user.uid,
      creationId: null,
      data: {
        characterName: duplicatedName,
        title: duplicatedName,
        characterDescription: data.characterDescription || '',
        coreMemory: data.coreMemory || '',
        firstMessage: data.firstMessage || '',
        shortIntro: data.shortIntro || data.shortDesc || '',
        tags: data.tags || data.hashtags || [],
        creatorComment: data.creatorComment || '',
        safetyFilter: data.safetyFilter !== false,
        unfiltered: data.safetyFilter === false,
        imageUrl: data.imageUrl || null,
        images: data.images || [],
        introImages: data.introImages || [],
        status: 'draft',
        authorName: window.userNickname || '익명',

        // 캐릭터 상세 정보
        character: {
          name: duplicatedName,
          gender: data.character?.gender || '여성',
          experience: data.character?.experience || data.character?.age || '',
          prompt: data.character?.prompt || '',
          additionalPrompt: data.character?.additionalPrompt || '',
          voiceId: data.character?.voiceId || '',
          imageUrl: data.character?.imageUrl || data.imageUrl || null
        },

        // 인트로 블록
        introBlocks: data.introBlocks || [],

        // 대화 예시
        exampleDialogues: data.exampleDialogues || [],

        // 상황별 이미지
        situationalImages: data.situationalImages || [],

        // 로어북
        lorebook: data.lorebook || []
      }
    };

    // Cloud Function 호출하여 저장
    const response = await authFetch(ENDPOINTS.saveCreation, {
      body: duplicateData
    });

    const result = await response.json();

    if (result.success && result.creationId) {
      hideGlobalLoading();
      showToast('점술가가 복제되었습니다');

      // 복제된 캐릭터 편집기로 이동
      const { openEditor } = await import('./editor.js');
      await openEditor(result.creationId);

      // 목록 새로고침
      loadCreations();
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '복제 실패');
      throw new Error(errorMsg);
    }
  } catch (error) {
    console.error('복제 오류:', error);
    hideGlobalLoading();
    showToast('복제에 실패했습니다: ' + error.message, true);
  }
}

// ============================================
// 아이템 메뉴
// ============================================

export function showItemMenu(event, creationId) {
  event.stopPropagation();

  // Dynamic import to avoid circular dependency
  import('./state.js').then(({ getCurrentCreations }) => {
    const creation = getCurrentCreations().find(c => c.id === creationId);
    if (!creation) return;

    // 기존 메뉴 제거
    const existingMenu = document.querySelector('.creator-item-menu');
    if (existingMenu) {
      existingMenu.remove();
    }

    const menu = document.createElement('div');
    menu.className = 'creator-item-menu';
    menu.innerHTML = `
      ${creation.status === 'public' ? `
        <button data-action="updateStatus" data-status="private">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          비공개로 전환
        </button>
      ` : `
        <button data-action="updateStatus" data-status="public">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
          공개하기
        </button>
      `}
      <button data-action="duplicateCreation">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>
        복제하기
      </button>
      <button class="danger" data-action="confirmDelete">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        삭제하기
      </button>
    `;

    // 이벤트 위임 설정
    menu.addEventListener('click', (e) => {
      const actionEl = e.target.closest('[data-action]');
      if (!actionEl) return;

      const action = actionEl.dataset.action;
      if (action === 'updateStatus') {
        updateStatus(creationId, actionEl.dataset.status);
      } else if (action === 'duplicateCreation') {
        duplicateCreation(creationId);
      } else if (action === 'confirmDelete') {
        confirmDelete(creationId);
      }
      menu.remove();
    });

    document.body.appendChild(menu);

    // 버튼 위치에 메뉴 표시
    const rect = event.target.closest('button').getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    // 메뉴가 화면 아래로 넘어가면 위에 표시
    let top = rect.bottom + 5;
    if (top + menuRect.height > window.innerHeight - 20) {
      top = rect.top - menuRect.height - 5;
    }

    menu.style.cssText = `
      position: fixed;
      top: ${top}px;
      right: ${window.innerWidth - rect.right}px;
      left: auto;
      bottom: auto;
      transform: none;
    `;

    // 바깥 클릭 시 닫기
    setTimeout(() => {
      document.addEventListener('click', function closeMenu() {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      });
    }, 0);
  });
}

export function closeItemMenu() {
  const menu = document.querySelector('.creator-item-menu');
  if (menu) {
    menu.remove();
  }
}
